// src/services/address/providers/brasilapi/adapter.js
// *******************************************************************************
// BrasilAPI Provider Adapter
// API Documentation: https://brasilapi.com.br/docs#tag/CEP
// *******************************************************************************

import fetchX from 'utils/fetchX';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

const REQUEST_TIMEOUT = 10000; // 10 seconds

/**
 * Fetches address data from BrasilAPI
 * @param {Object} options - Request options
 * @param {string} options.cep - CEP number (8 digits, no mask)
 * @returns {Promise<Object>} Raw response from BrasilAPI
 * @throws {Error} When API fails (timeout, connection error, etc)
 */
export const searchAddress = async options => {
  const { cep } = options;

  if (!cep || !/^\d{8}$/.test(cep)) {
    throw new Error('CEP inválido. Deve conter 8 dígitos.');
  }

  const url = mergeUrlParamsQuery({
    url: API.PROVIDERS.CEP.BRASILAPI,
    params: { cep },
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(new Error('Request timeout'));
  }, REQUEST_TIMEOUT);

  try {
    const response = await fetchX(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // BrasilAPI returns 404 when CEP not found
      if (response.status === 404) {
        return {
          provider: 'brasilapi',
          success: false,
          apiWorked: true, // API worked, but CEP doesn't exist
          error: 'CEP não encontrado',
        };
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      provider: 'brasilapi',
      success: true,
      ...data,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    return {
      provider: 'brasilapi',
      success: false,
      error: error.name === 'AbortError' ? 'Timeout' : error.message,
    };
  }
};

/**
 * Normalizes request payload to adapter format
 * @param {Object} base - Base request object
 * @param {string} base.cep - CEP number
 * @returns {Object} Normalized request
 */
export const normalizeRequestPayload = base => {
  let cep = base?.cep ?? null;

  if (typeof cep === 'string') {
    // Remove non-numeric characters
    cep = cep.replace(/\D/g, '');
  }

  if (!cep || !/^\d{8}$/.test(cep)) {
    throw new Error('CEP inválido');
  }

  return { cep };
};

/**
 * Normalizes response to universal POJO format
 * @param {Object} res - Raw response from BrasilAPI
 * @returns {Object} Universal POJO
 *
 * BrasilAPI Response format:
 * {
 *   cep: "01310-100",
 *   state: "SP",
 *   city: "São Paulo",
 *   neighborhood: "Bela Vista",
 *   street: "Avenida Paulista",
 *   service: "brasilapi"
 * }
 */
export const normalizeResponseBody = res => {
  if (!res || typeof res !== 'object') {
    return {
      provider: 'brasilapi',
      success: false,
      error: 'Invalid response',
    };
  }

  // If error in response
  if (res.success === false || res.error) {
    return {
      provider: 'brasilapi',
      success: false,
      apiWorked: res.apiWorked || false, // Preserve apiWorked flag
      error: res.error || 'Unknown error',
    };
  }

  // Detect if CEP covers multiple addresses (BrasilAPI doesn't have this indicator directly)
  // Usually when street is empty, it means city-wide CEP
  const hasMultipleAddresses = !res.street || res.street.trim() === '';

  return {
    // Identification
    zip: res.cep?.replace(/\D/g, '') || '',
    source: 'brasilapi',
    success: true,

    // Address fields (universal POJO keys)
    street: res.street || '',
    unit: '', // BrasilAPI doesn't provide unit
    neighborhood: res.neighborhood || '',
    city: res.city || '',
    state: res.state || '',
    stateName: '', // BrasilAPI doesn't provide full state name
    region: '', // BrasilAPI doesn't provide region

    // Additional codes (BrasilAPI doesn't provide these)
    areaCode: '',
    ibgeCode: '',
    giaCode: '',
    siafiCode: '',

    // Metadata
    hasMultipleAddresses,
    error: null,
  };
};

export default {
  searchAddress,
  normalizeRequestPayload,
  normalizeResponseBody,
};
